import React, { useState, useEffect, useRef } from "react";
import Dashboard from "../Dashbard";
import Loader from "../../../../components/common/Loader";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Chip,
  IconButton,
  Typography,
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import {
  Add,
  Delete,
  Edit,
  Refresh,
  Today,
  Event,
  People,
  AccessTime,
  Close,
  VideoCall,
} from "@mui/icons-material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import { format, parseISO, isBefore, isAfter } from "date-fns";
import "./CalendarView.css";
import { Alert } from "@mui/material";
import { dashboardActions } from "../../../../store/dashboard/dashboardActions";
import { useDispatch } from "react-redux";
import { FrolaEditor } from "../../../../components/common";

function CalendarView(props) {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState("dayGridMonth");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const calendarRef = useRef(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    start: "",
    end: "",
    description: "",
    attendees: [],
    isAllDay: false,
    category: "meeting",
    meetingLink: "",
  });
  const [newAttendeeEmail, setNewAttendeeEmail] = useState("");
  const [formErrors, setFormErrors] = useState({
    title: "",
    start: "",
    end: "",
  });

  // Load calendar events from YOUR backend API
  useEffect(() => {
    loadEvents();
  }, []);

  // Add effect to handle popover close when clicking on an event
  useEffect(() => {
    const handlePopoverEventClick = () => {
      // Find and close any open popovers when an event is clicked
      const popovers = document.querySelectorAll(".fc-more-popover");
      popovers.forEach(popover => {
        if (popover.parentNode) {
          popover.parentNode.removeChild(popover);
        }
      });
    };

    // Add event listener to the document
    document.addEventListener("click", e => {
      if (e.target.closest(".fc-more-popover .fc-event")) {
        setTimeout(handlePopoverEventClick, 100);
      }
    });

    return () => {
      document.removeEventListener("click", handlePopoverEventClick);
    };
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);

      const userId = localStorage.getItem("id");
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await dispatch(
        dashboardActions.onGetUserEvents(userId, timeZone),
      );

      if (response?.data?.success) {
        const transformedEvents = transformEvents(response.data.data);

        setEvents(transformedEvents);
      } else {
        console.error("Failed to load events:", response?.data?.message);
        setSnackbar({
          open: true,
          message: response?.data?.message || "Failed to load calendar events",
          severity: "error",
        });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error loading events:", error);
      setSnackbar({
        open: true,
        message: "Failed to load calendar events",
        severity: "error",
      });
      setLoading(false);
    }
  };

  const transformEvents = apiEvents => {
    return apiEvents.map(event => {
      return {
        id: event.id,
        title: event.subject,
        start: event.startDate,
        end: event.endDate,
        extendedProps: {
          description: event.body || "",
          attendees: event.attendees || [],
          category: event.category || "meeting",
          status: event.status || "confirmed",
          meetingLink: event.meetinglink || "", // Make sure this is set
          originalData: {
            id: event.id,
            userId: event.userId,
            eventId: event.eventId,
            calendarKey: event.calendarKey,
            subject: event.subject,
            body: event.body,
            startDate: event.startDate,
            endDate: event.endDate,
            attendees: event.attendees,
            meetingLink: event.meetinglink,
          },
        },
        backgroundColor: getEventColor(event.category, event.status),
        borderColor: getEventBorderColor(event.category, event.status),
        textColor: "#ffffff",
        allDay: event.isAllDay || false,
      };
    });
  };

  // Get event color based on category and status
  const getEventColor = (category, status) => {
    if (status === "cancelled") return "#f44336";

    const colors = {
      meeting: "#2196f3",
      appointment: "#4caf50",
      reminder: "#ff9800",
      holiday: "#9c27b0",
      personal: "#00bcd4",
      work: "#3f51b5",
      default: "#607d8b",
    };

    return colors[category] || colors.default;
  };

  const getEventBorderColor = (category, status) => {
    if (status === "cancelled") return "#d32f2f";

    const colors = {
      meeting: "#1976d2",
      appointment: "#388e3c",
      reminder: "#f57c00",
      holiday: "#7b1fa2",
      personal: "#0097a7",
      work: "#303f9f",
      default: "#455a64",
    };

    return colors[category] || colors.default;
  };

  const handleDateClick = arg => {
    const startDate = arg.date;
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour later

    setEventForm({
      title: "",
      start: formatDateForInput(startDate),
      end: formatDateForInput(endDate),
      description: "",
      attendees: [],
      isAllDay: false,
      category: "meeting",
      meetingLink: "",
    });
    setFormErrors({
      title: "",
      start: "",
      end: "",
    });
    setSelectedEvent(null);
    setOpenDialog(true);
  };

  const formatDateForInput = date => {
    return format(date, "yyyy-MM-dd'T'HH:mm");
  };

  const handleEventClick = clickInfo => {
    // Close any open popovers
    const popovers = document.querySelectorAll(".fc-more-popover");
    popovers.forEach(popover => {
      if (popover.parentNode) {
        popover.parentNode.removeChild(popover);
      }
    });

    const event = clickInfo.event;

    // Get meetingLink from extendedProps
    const meetingLink = event.extendedProps.meetingLink || "";

    console.log("Meeting Link from event:", meetingLink); // Debug log

    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start ? formatDateForInput(event.start) : "",
      end: event.end ? formatDateForInput(event.end) : "",
      description: event.extendedProps.description || "",
      attendees: event.extendedProps.attendees || [],
      category: event.extendedProps.category || "meeting",
      isAllDay: event.allDay || false,
      meetingLink: meetingLink, // یہاں meetingLink set کر رہے ہیں
      extendedProps: event.extendedProps,
    });

    setEventForm({
      title: event.title,
      start: event.start ? formatDateForInput(event.start) : "",
      end: event.end ? formatDateForInput(event.end) : "",
      description: event.extendedProps.description || "",
      attendees: event.extendedProps.attendees || [],
      category: event.extendedProps.category || "meeting",
      isAllDay: event.allDay || false,
      meetingLink: meetingLink, // یہاں بھی meetingLink set کر رہے ہیں
    });

    setFormErrors({
      title: "",
      start: "",
      end: "",
    });

    setOpenDialog(true);
  };

  const handleFormChange = e => {
    const { name, value, type, checked } = e.target;

    // Keep your title trim behavior
    const processedValue = name === "title" ? value.trimStart() : value;

    const updatedForm = {
      ...eventForm,
      [name]: type === "checkbox" ? checked : processedValue,
    };

    setEventForm(updatedForm);

    // Clear error for the current field as user edits
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }

    // Real-time date validation with latest values
    if (name === "start" || name === "end") {
      validateDates(updatedForm);
    }
  };

  // Validate dates - Only check end > start
  const validateDates = (nextForm = eventForm) => {
    const errors = { ...formErrors };

    errors.start = "";
    errors.end = "";

    if (nextForm.start && nextForm.end) {
      if (!isEndAfterStart(nextForm.start, nextForm.end)) {
        errors.end = "End date & time must be after start date & time";
      }
    }

    setFormErrors(errors);
    return !errors.end;
  };

  const handleAddAttendee = () => {
    const email = newAttendeeEmail.trim().toLowerCase();

    // If email is empty, just clear the field (no validation error)
    if (!email) {
      setNewAttendeeEmail("");
      return;
    }

    // Only validate if email is provided
    if (!isValidEmail(email)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address",
        severity: "warning",
      });
      return;
    }

    // Check for duplicate email
    const isDuplicate = eventForm.attendees.some(
      attendee => attendee.email.toLowerCase() === email,
    );

    if (isDuplicate) {
      setSnackbar({
        open: true,
        message: "This email is already added to the attendees list",
        severity: "warning",
      });
      return;
    }

    setEventForm(prev => ({
      ...prev,
      attendees: [...prev.attendees, { email, status: "pending" }],
    }));
    setNewAttendeeEmail("");
  };

  const handleRemoveAttendee = index => {
    setEventForm(prev => ({
      ...prev,
      attendees: prev.attendees.filter((_, i) => i !== index),
    }));
  };

  const isValidEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isEndAfterStart = (start, end) => {
    if (!start || !end) return true;
    return new Date(end).getTime() > new Date(start).getTime();
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Title validation
    if (!eventForm.title.trim()) {
      errors.title = "Event title is required";
      isValid = false;
    } else if (eventForm.title.trim().length < 2) {
      errors.title = "Event title must be at least 2 characters";
      isValid = false;
    }

    // Start date/time required
    if (!eventForm.start) {
      errors.start = "Start date/time is required";
      isValid = false;
    }

    // End date/time required
    if (!eventForm.end) {
      errors.end = "End date/time is required";
      isValid = false;
    }

    // Date-time logic (single source of truth)
    if (
      eventForm.start &&
      eventForm.end &&
      !isEndAfterStart(eventForm.start, eventForm.end)
    ) {
      errors.end = "End date & time must be after start date & time";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddAttendee();
    }
  };

  const handleJoinMeeting = () => {
    const meetingLink = selectedEvent?.meetingLink || eventForm?.meetingLink;

    console.log("Joining meeting with link:", meetingLink); // Debug log

    if (meetingLink && meetingLink.trim() !== "") {
      // Open meeting link in new tab
      window.open(meetingLink, "_blank", "noopener,noreferrer");
    } else {
      setSnackbar({
        open: true,
        message: "No meeting link available for this event",
        severity: "warning",
      });
    }
  };

  const handleSubmit = async () => {
    // Validate entire form
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fix the errors in the form",
        severity: "error",
      });
      return;
    }

    try {
      setLoading(true);

      // Prepare event data for YOUR backend API
      const eventData = {
        id: selectedEvent?.extendedProps?.originalData?.id || 0, // Database ID for updates, 0 for new
        subject: eventForm.title.trim(),
        body: eventForm.description,
        attendees: eventForm.attendees.map(attendee => ({
          email: attendee.email.trim(),
          required: true,
        })),
        startDateTime: eventForm.start, // Just send the raw string
        endDateTime: eventForm.end,
        isRecurring: false,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        calenderKey:
          selectedEvent?.extendedProps?.originalData?.calendarKey || "",
        eventUniqueKey:
          selectedEvent?.extendedProps?.originalData?.eventId || "", // Microsoft Graph eventId for updates
        email: "", // Get from user profile if available
        userId: localStorage.getItem("id") || "",
        userOId: "", // Add if you have OID
        meetingLink: eventForm.meetingLink || "",
      };

      // Dispatch the API call
      const response = await dispatch(
        dashboardActions.onAddUpdateEvents(eventData),
      );
      if (response?.data?.success) {
        // Reload events after successful save
        await loadEvents();

        setSnackbar({
          open: true,
          message: selectedEvent
            ? "Event updated successfully!"
            : "Event created successfully!",
          severity: "success",
        });

        setOpenDialog(false);
        setSelectedEvent(null);
        setEventForm({
          title: "",
          start: "",
          end: "",
          description: "",
          attendees: [],
          isAllDay: false,
          category: "meeting",
        });
        setFormErrors({
          title: "",
          start: "",
          end: "",
        });
      } else {
        throw new Error(response?.data?.message || "Failed to save event");
      }
    } catch (error) {
      console.error("Error saving event:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to save event. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        setLoading(true);
        const response = await dispatch(
          dashboardActions.onDeleteUserEvents(
            selectedEvent?.extendedProps?.originalData?.eventId,
          ),
        );
        if (response?.data?.success) {
          // Reload events after successful delete
          await loadEvents();

          setSnackbar({
            open: true,
            message: "Event deleted successfully!",
            severity: "success",
          });

          setOpenDialog(false);
          setSelectedEvent(null);
          setEventForm({
            title: "",
            start: "",
            end: "",
            description: "",
            attendees: [],
            isAllDay: false,
            category: "meeting",
          });
        } else {
          throw new Error(response?.data?.message || "Failed to delete event");
        }
      } catch (error) {
        console.error("Error deleting event:", error);
        setSnackbar({
          open: true,
          message: error.message || "Failed to delete event. Please try again.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEventDrop = async dropInfo => {
    try {
      const eventId = dropInfo.event.id;
      const event = events.find(e => e.id.toString() === eventId.toString());
      const newStart = dropInfo.event.start;
      const newEnd =
        dropInfo.event.end || new Date(newStart.getTime() + 60 * 60 * 1000);

      if (event) {
        // Prepare update data for drag & drop
        const updateData = {
          id: event.extendedProps?.originalData?.id || 0,
          subject: event.title,
          body: event.extendedProps?.description || "",
          attendees:
            event.extendedProps?.attendees?.map(attendee => ({
              email: attendee.email,
              required: true,
            })) || [],
          startDateTime: newStart.toISOString(),
          endDateTime: newEnd.toISOString(),
          isRecurring: false,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          calenderKey: event.extendedProps?.originalData?.calendarKey || "",
          eventUniqueKey: event.extendedProps?.originalData?.eventId || "",
          email: "",
          userId: localStorage.getItem("id") || "",
          userOId: "",
          meetingLink: event.extendedProps?.meetingLink || "",
        };

        const response = await dispatch(
          dashboardActions.onAddUpdateEvents(updateData),
        );

        if (response?.data?.success) {
          // Reload events to get updated data
          await loadEvents();

          setSnackbar({
            open: true,
            message: "Event rescheduled successfully!",
            severity: "success",
          });
        } else {
          throw new Error(
            response?.data?.message || "Failed to reschedule event",
          );
        }
      }
    } catch (error) {
      console.error("Error rescheduling event:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to reschedule event",
        severity: "error",
      });
      dropInfo.revert();
    }
  };

  const handleViewChange = viewName => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(viewName);
      setView(viewName);
    }
  };

  const handleToday = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.today();
    }
  };

  const categories = [
    { value: "meeting", label: "Meeting", color: "#2196f3" },
    { value: "appointment", label: "Appointment", color: "#4caf50" },
    { value: "reminder", label: "Reminder", color: "#ff9800" },
    { value: "holiday", label: "Holiday", color: "#9c27b0" },
    { value: "personal", label: "Personal", color: "#00bcd4" },
    { value: "work", label: "Work", color: "#3f51b5" },
  ];

  return (
    <Dashboard>
      <div className='calendar-container'>
        {isLoading && <Loader />}

        {/* Calendar Header */}
        <div className='calendar-header'>
          <div className='calendar-title'>
            <Typography
              variant='h5'
              style={{ fontWeight: 600, color: "#1a365d" }}>
              Calendar
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              Manage your schedule and events
            </Typography>
          </div>

          <div className='calendar-actions'>
            <Button
              variant='outlined'
              startIcon={<Today />}
              onClick={handleToday}
              style={{ textTransform: "none" }}>
              Today
            </Button>

            <Button
              variant='outlined'
              startIcon={<Refresh />}
              onClick={loadEvents}
              disabled={isLoading}
              style={{ textTransform: "none" }}>
              Refresh
            </Button>

            <div className='view-buttons'>
              {["dayGridMonth", "timeGridWeek", "timeGridDay", "listWeek"].map(
                viewType => (
                  <Button
                    key={viewType}
                    variant={view === viewType ? "contained" : "outlined"}
                    onClick={() => handleViewChange(viewType)}
                    size='small'
                    style={{ textTransform: "none" }}>
                    {viewType === "dayGridMonth"
                      ? "Month"
                      : viewType === "timeGridWeek"
                        ? "Week"
                        : viewType === "timeGridDay"
                          ? "Day"
                          : "List"}
                  </Button>
                ),
              )}
            </div>

            <Button
              variant='contained'
              color='primary'
              startIcon={<Add />}
              onClick={() => {
                const now = new Date();
                const later = new Date(now.getTime() + 60 * 60 * 1000);

                setEventForm({
                  title: "",
                  start: formatDateForInput(now),
                  end: formatDateForInput(later),
                  description: "",
                  attendees: [],
                  isAllDay: false,
                  category: "meeting",
                  meetingLink: "",
                });
                setFormErrors({
                  title: "",
                  start: "",
                  end: "",
                });
                setSelectedEvent(null);
                setOpenDialog(true);
              }}
              style={{ textTransform: "none" }}>
              New Event
            </Button>
          </div>
        </div>

        {/* Calendar View */}
        <Paper elevation={2} className='calendar-wrapper'>
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              interactionPlugin,
              listPlugin,
            ]}
            initialView='dayGridMonth'
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
            }}
            events={events}
            editable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            nowIndicator={true}
            height='70vh'
            dateClick={handleDateClick}
            eventClick={handleEventClick}
            eventDrop={handleEventDrop}
            eventResize={handleEventDrop}
            moreLinkClick={info => {
              // Custom handling for more link click
              console.log("More link clicked:", info);
            }}
            eventContent={eventInfo => (
              <div className='fc-event-content'>
                <div className='fc-event-title'>
                  <Event style={{ fontSize: 12, marginRight: 4 }} />
                  {eventInfo.event.title}
                </div>
              </div>
            )}
            eventDidMount={info => {
              // Add tooltip to event element
              info.el.title = info.event.title;
            }}
          />
        </Paper>

        {/* Event Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth='sm'
          fullWidth>
          {/* Dialog Header */}
          <div className='dialog-header'>
            <div className='dialog-header-content'>
              <Event color='primary' />
              <Typography variant='h6' className='dialog-title'>
                {selectedEvent ? "Edit Event" : "Create New Event"}
              </Typography>
            </div>
            <IconButton
              className='dialog-close-btn'
              onClick={() => setOpenDialog(false)}
              size='small'>
              <Close />
            </IconButton>
          </div>

          <DialogContent dividers style={{ padding: "24px" }}>
            <div className='event-form'>
              {/* Event Title */}
              <TextField
                fullWidth
                label='Event Title *'
                name='title'
                value={eventForm.title}
                onChange={handleFormChange}
                className='form-field-spacing'
                required
                variant='outlined'
                autoFocus
                margin='normal'
                error={!!formErrors.title}
                helperText={formErrors.title}
                inputProps={{
                  maxLength: 100,
                }}
              />

              {/* Date/Time Section */}
              <div className='form-section'>
                <Typography className='form-section-title'>
                  <AccessTime /> Date & Time
                </Typography>

                <div className='form-row'>
                  <TextField
                    fullWidth
                    label='Start Date & Time *'
                    type='datetime-local'
                    name='start'
                    value={eventForm.start}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                    variant='outlined'
                    required
                    margin='normal'
                    error={!!formErrors.start}
                    helperText={formErrors.start}
                  />

                  <TextField
                    fullWidth
                    label='End Date & Time *'
                    type='datetime-local'
                    name='end'
                    value={eventForm.end}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                    variant='outlined'
                    required
                    margin='normal'
                    error={!!formErrors.end}
                    helperText={formErrors.end}
                  />
                </div>
              </div>

              {/* All Day Checkbox */}
              {/* <FormControlLabel
                className='all-day-checkbox'
                control={
                  <Checkbox
                    checked={eventForm.isAllDay}
                    onChange={handleFormChange}
                    name='isAllDay'
                    color='primary'
                  />
                }
                label='All Day Event'
              /> */}

              {/* Attendees Section */}
              <div className='form-section'>
                <Typography className='form-section-title'>
                  <People /> Attendees
                </Typography>

                <div className='attendee-input-container'>
                  <TextField
                    className='attendee-input'
                    size='small'
                    placeholder='Enter email address (optional)'
                    value={newAttendeeEmail}
                    onChange={e => setNewAttendeeEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    variant='outlined'
                    fullWidth
                    margin='normal'
                    error={
                      newAttendeeEmail.trim() &&
                      !isValidEmail(newAttendeeEmail.trim())
                    }
                    helperText={
                      newAttendeeEmail.trim() &&
                      !isValidEmail(newAttendeeEmail.trim())
                        ? "Invalid email format"
                        : ""
                    }
                  />
                  <Button
                    className='attendee-add-btn'
                    variant='outlined'
                    onClick={handleAddAttendee}
                    disabled={
                      newAttendeeEmail.trim() &&
                      !isValidEmail(newAttendeeEmail.trim())
                    }>
                    Add
                  </Button>
                </div>

                <div className='attendee-chips'>
                  {eventForm.attendees.map((attendee, index) => (
                    <Chip
                      key={index}
                      label={attendee.email}
                      onDelete={() => handleRemoveAttendee(index)}
                      className='attendee-chip'
                      deleteIcon={<Close />}
                    />
                  ))}
                </div>
              </div>

              {/* Description */}
              <div className='form-section'>
                <Typography className='form-section-title'>
                  Description
                </Typography>
                <div className='description-editor-container'>
                  <FrolaEditor
                    model={eventForm.description}
                    onHandleModelChange={model =>
                      setEventForm(prev => ({ ...prev, description: model }))
                    }
                    placeholder='Add meeting agenda, notes, or details...'
                    maxWidth='100%'
                    charCounterMax={5000}
                  />
                </div>
              </div>
            </div>
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions className='dialog-actions'>
            {selectedEvent &&
              selectedEvent.meetingLink &&
              selectedEvent.meetingLink.trim() !== "" && (
                <Button
                  onClick={handleJoinMeeting}
                  color='primary'
                  startIcon={<VideoCall />}
                  variant='contained'
                  style={{
                    textTransform: "none",
                    backgroundColor: "#4caf50",
                    "&:hover": {
                      backgroundColor: "#45a049",
                    },
                  }}>
                  Join Meeting
                </Button>
              )}
            {selectedEvent && (
              <Button
                onClick={handleDelete}
                color='secondary'
                startIcon={<Delete />}
                variant='outlined'
                style={{ textTransform: "none" }}>
                Delete
              </Button>
            )}

            <Button
              onClick={() => setOpenDialog(false)}
              variant='outlined'
              style={{ textTransform: "none" }}>
              Cancel
            </Button>

            <Button
              onClick={handleSubmit}
              color='primary'
              variant='contained'
              disabled={
                !eventForm.title.trim() ||
                !eventForm.start ||
                !eventForm.end ||
                isLoading ||
                !!formErrors.title ||
                !!formErrors.start ||
                !!formErrors.end
              }
              style={{ textTransform: "none", padding: "8px 24px" }}>
              {selectedEvent ? "Update Event" : "Create Event"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant='filled'
            sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </Dashboard>
  );
}

export default CalendarView;
